import { type CompanyDto, type ConnectionCheckDto, type ConnectionDto, connectionUpdateSchema, EMAIL_MODES, type EmailMode, INTEGRATION_MODES, type IntegrationMode } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Divider, Group, List, PasswordInput, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { IconPlugConnected } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { formatDateTime } from '../format';
import { ask } from '../confirm';
import { notifyOk } from '../notify';
import { QueryState } from './QueryState';

export const connectionBadge = (c: ConnectionDto | undefined, failed = false) =>
  failed ? (
    <Badge variant="light" color="red" size="sm">
      Fakturownia: nie udało się sprawdzić
    </Badge>
  ) : !c ? (
    <Badge variant="light" color="gray" size="sm">
      Fakturownia: …
    </Badge>
  ) : c.mode === 'wylaczona' ? (
    <Badge variant="light" color="gray" size="sm">
      Fakturownia: wyłączona
    </Badge>
  ) : (
    <Badge variant="light" color={c.status === 'ok' ? (c.mode === 'test' ? 'yellow' : 'green') : c.status === 'blad' ? 'red' : 'gray'} size="sm">
      Fakturownia · {INTEGRATION_MODES[c.mode]} · {c.status === 'ok' ? 'połączono' : c.status === 'blad' ? 'błąd' : 'nie sprawdzono'}
    </Badge>
  );

export const useConnection = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ['integration', companyId], queryFn: () => api<ConnectionDto>(`/companies/${companyId}/integrations/fakturownia`), enabled });

/** Połączenie firmy z Fakturownią: tryb, prefiks konta, token (tylko zapis), dział wystawcy, „Sprawdź połączenie”. */
export function FakturowniaSettings({ company }: { company: CompanyDto }) {
  const qc = useQueryClient();
  const q = useConnection(company.id);
  const [mode, setMode] = useState<IntegrationMode>('wylaczona');
  const [prefix, setPrefix] = useState('');
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [emailMode, setEmailMode] = useState<EmailMode>('crm');
  const [autoSend, setAutoSend] = useState(true);
  const [check, setCheck] = useState<ConnectionCheckDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setMode(q.data.mode);
    setPrefix(q.data.accountPrefix);
    setDepartmentId(q.data.departmentId ? String(q.data.departmentId) : null);
    setEmailMode(q.data.emailMode);
    setAutoSend(q.data.autoSend);
  }, [q.data]);

  const url = `/companies/${company.id}/integrations/fakturownia`;
  const save = useMutation({
    mutationFn: async () => {
      const input = connectionUpdateSchema.parse({ mode, accountPrefix: prefix, departmentId: departmentId ? Number(departmentId) : null, token: token || undefined, emailMode, autoSend });
      await api<ConnectionDto>(url, { method: 'PUT', body: input });
      // po zapisie od razu sprawdzenie połączenia
      return mode === 'wylaczona' ? null : api<ConnectionCheckDto>(`${url}/check`, { method: 'POST' });
    },
    onSuccess: (r) => {
      setToken('');
      setError(null);
      setCheck(r);
      void qc.invalidateQueries({ queryKey: ['integration', company.id] });
      if (!r) notifyOk('Integracja wyłączona');
      else if (r.ok) notifyOk('Zapisano — połączenie z Fakturownią działa');
    },
    onError: (e) => setError(e instanceof Error && 'issues' in e ? 'Prefiks konta: litery, cyfry i myślniki (np. ekocrm)' : errorText(e)),
  });
  const test = useMutation({
    mutationFn: () => api<ConnectionCheckDto>(`${url}/check`, { method: 'POST' }),
    onSuccess: (r) => {
      setCheck(r);
      void qc.invalidateQueries({ queryKey: ['integration', company.id] });
    },
    onError: (e) => setError(errorText(e)),
  });

  const c = q.data;
  if (!c)
    return (
      <QueryState isLoading={!q.error} error={q.error} lines={2}>
        {null}
      </QueryState>
    );
  const departments = check?.departments ?? [];
  return (
    <Stack gap="sm">
      <Group justify="space-between">
        {connectionBadge(c)}
        {c.lastCheckAt && (
          <Text size="xs" c="dimmed">
            sprawdzono {formatDateTime(c.lastCheckAt)}
          </Text>
        )}
      </Group>
      {!c.keyConfigured && (
        <Alert color="red" title="Brak klucza szyfrowania na serwerze">
          Skontaktuj się z administratorem serwera — bez klucza szyfrowania nie zapiszesz tokenu.
        </Alert>
      )}
      <Text size="sm" c="dimmed">
        Faktury z CRM wystawi Fakturownia — nada numer, a jeśli ma włączony KSeF, wyśle je do KSeF. Token API znajdziesz w Fakturowni: Ustawienia → Ustawienia konta → Integracja → Kod autoryzacyjny API. Najlepiej
        wygeneruj go dla osobnego użytkownika (np. „API-CRM”) z dostępem tylko do faktur i klientów.
      </Text>
      <Select
        label="Tryb"
        value={mode}
        onChange={(v) => v && setMode(v as IntegrationMode)}
        data={Object.entries(INTEGRATION_MODES).map(([value, label]) => ({ value, label }))}
        description={mode === 'test' ? 'Konto testowe — faktury w CRM oznaczone jako testowe' : mode === 'produkcja' ? 'Prawdziwe faktury (z KSeF, jeśli włączony w Fakturowni)' : undefined}
        allowDeselect={false}
      />
      <TextInput
        label="Prefiks konta"
        description="Pierwsza część adresu konta, np. ekocrm z ekocrm.fakturownia.pl"
        value={prefix}
        onChange={(e) => setPrefix(e.currentTarget.value)}
        rightSection={<Text size="xs" c="dimmed" pr="xl">.fakturownia.pl</Text>}
        rightSectionWidth={110}
      />
      <PasswordInput
        label="Token API"
        description={c.tokenHint ? `Zapisany: ••••${c.tokenHint} (${c.tokenSetAt ? formatDateTime(c.tokenSetAt) : ''}) — wpisz nowy, aby zastąpić` : 'Token jest szyfrowany i nie da się go później odczytać — tylko zastąpić'}
        placeholder={c.tokenHint ? 'bez zmian' : 'wklej token z Fakturowni'}
        value={token}
        onChange={(e) => setToken(e.currentTarget.value)}
        autoComplete="off"
      />
      <Select
        label="Firma / dział wystawcy w Fakturowni"
        placeholder={departments.length ? 'wybierz' : c.departmentId ? `ID ${c.departmentId}` : 'domyślny (główna firma konta)'}
        description="Lista pojawi się po „Sprawdź połączenie”. Bez wyboru faktury wystawi główna firma konta."
        value={departmentId}
        onChange={setDepartmentId}
        clearable
        data={[
          ...departments.map((d) => ({ value: String(d.id), label: `${d.name}${d.taxNo ? ` (NIP ${d.taxNo})` : ''}` })),
          ...(departmentId && !departments.some((d) => String(d.id) === departmentId) ? [{ value: departmentId, label: `ID ${departmentId}` }] : []),
        ]}
      />
      {departments.length > 0 && company.nip && departmentId && departments.find((d) => String(d.id) === departmentId)?.taxNo.replace(/\D/g, '') !== company.nip.replace(/\D/g, '') && (
        <Alert color="orange">NIP wybranego działu w Fakturowni różni się od NIP firmy w CRM ({company.nip}). Sprawdź, czy to właściwa firma.</Alert>
      )}
      <Divider label="Wysyłka dokumentów do klienta" labelPosition="left" />
      <Select
        label="Kto wysyła pro formy i faktury"
        value={emailMode}
        onChange={(v) => v && setEmailMode(v as EmailMode)}
        data={Object.entries(EMAIL_MODES).map(([value, label]) => ({ value, label }))}
        description={
          emailMode === 'crm'
            ? 'PDF z Fakturowni w załączniku wiadomości z CRM — nie zużywa limitu wysyłek Fakturowni'
            : emailMode === 'fakturownia'
              ? 'Wysyłka i historia w Fakturowni (limit wysyłek wg planu; faktury dla firm po nadaniu numeru KSeF)'
              : undefined
        }
        allowDeselect={false}
      />
      <Switch label="Wysyłaj automatycznie po wystawieniu" checked={autoSend} onChange={(e) => setAutoSend(e.currentTarget.checked)} disabled={emailMode === 'brak'} />
      {error && <Alert color="red">{error}</Alert>}
      {check && (
        <Alert color={check.ok ? 'green' : 'red'} icon={<IconPlugConnected />}>
          {check.message}
          {check.ok && departments.length > 0 && (
            <List size="xs" mt={4}>
              {departments.map((d) => (
                <List.Item key={d.id}>
                  {d.name} {d.taxNo ? `— NIP ${d.taxNo}` : ''} (ID {d.id})
                </List.Item>
              ))}
            </List>
          )}
        </Alert>
      )}
      {c.status === 'blad' && !check && <Alert color="red">{c.lastError || 'Ostatnie połączenie nie powiodło się'}</Alert>}
      <Group justify="space-between">
        <Button variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={c.mode === 'wylaczona' || !c.tokenHint}>
          Sprawdź połączenie
        </Button>
        <Button
          onClick={async () => {
            // przejście na produkcję: od teraz prawdziwe faktury (KSeF) i e-maile do klientów
            if (mode === 'produkcja' && c.mode !== 'produkcja') {
              const ok = await ask(
                `Tryb produkcyjny: faktury wystawiane z CRM będą prawdziwymi dokumentami księgowymi (a jeśli Fakturownia ma włączony KSeF — trafią do KSeF i nie da się ich usunąć, tylko skorygować).${emailMode !== 'brak' && autoSend ? ' Pro formy i faktury będą automatycznie wysyłane e-mailem do klientów.' : ''} Upewnij się, że prefiks konta i firma wystawcy są właściwe.`,
                { title: 'Włączyć tryb produkcyjny?', confirm: 'Włącz produkcję', danger: true },
              );
              if (!ok) return;
            }
            save.mutate();
          }}
          loading={save.isPending}
          disabled={mode !== 'wylaczona' && !prefix}
        >
          Zapisz
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Konto testowe Fakturowni: <Anchor href="https://fakturownia.pl" target="_blank" rel="noopener" size="xs">fakturownia.pl</Anchor> (okres próbny). Najpierw przetestuj w trybie „Testowa”.
      </Text>
    </Stack>
  );
}
